import type { Locale } from "date-fns";

import { de as dateFnsDe, enUS as dateFnsEnUs } from "date-fns/locale";
import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import deTranslations from "@/locales/de/translation";
import enTranslations from "@/locales/en/translation";

export const DEFAULT_LOCALE = "de";

const INTL_LOCALES = {
  de: "de-DE",
  en: "en-US",
} as const;

const DATE_FNS_LOCALES: Record<string, Locale> = {
  de: dateFnsDe,
  en: dateFnsEnUs,
};

const resources: Resource = {
  de: {
    translation: deTranslations,
  },
  en: {
    translation: enTranslations,
  },
} as const;

export type AppLocale = keyof typeof INTL_LOCALES;

function baseI18nOptions(language: AppLocale) {
  return {
    resources,
    lng: language,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: Object.keys(INTL_LOCALES),
    interpolation: {
      escapeValue: false,
    },
    returnNull: false,
  } as const;
}

export function normalizeLocale(locale?: string | null): AppLocale {
  if (!locale) return DEFAULT_LOCALE;

  const normalized = locale.toLowerCase().split("-")[0];
  return normalized === "en" ? "en" : DEFAULT_LOCALE;
}

export function getCurrentLocale(): AppLocale {
  return normalizeLocale(
    i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE
  );
}

export function getIntlLocale(locale?: string | null): string {
  return INTL_LOCALES[normalizeLocale(locale)];
}

export function getDateFnsLocale(locale?: string | null): Locale {
  return DATE_FNS_LOCALES[normalizeLocale(locale)];
}

export function syncDocumentLanguage(locale?: string | null) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = normalizeLocale(locale);
}

export function ensureI18nInitialized(locale?: string | null) {
  const language = normalizeLocale(locale);

  if (!i18n.isInitialized) {
    const options = {
      ...baseI18nOptions(language),
      initImmediate: false,
    } as Parameters<typeof i18n.init>[0] & { initImmediate: boolean };

    void i18n.use(initReactI18next).init(options);
  }

  return i18n;
}

export async function initializeI18n(locale?: string | null) {
  const language = normalizeLocale(locale);

  if (!i18n.isInitialized) {
    ensureI18nInitialized(language);
  } else if (getCurrentLocale() !== language) {
    await i18n.changeLanguage(language);
  }

  syncDocumentLanguage(language);
  return i18n;
}

ensureI18nInitialized();

export { i18n };
